<template>
  <div class="">
    <div class="py-16 lg:py-18">
      <div
        v-if="data?.title || !hideArrows"
        class="container mb-7.5 flex items-center justify-between"
      >
        <h2
          v-if="data?.title"
          class="flex w-full justify-center text-lg capitalize text-gray-dark lg:text-[27px] lg:tracking-wider"
          :style="{ textAlign: data?.title_position || 'left' }"
        >
          <span
            class="block whitespace-pre-wrap text-center"
            v-html="data.title"
          />
        </h2>
        <div v-if="!hideArrows" class="shrink-0 max-lg:hidden">
          <button
            ref="prevEl"
            :aria-label="$t('previousSlide')"
            class="mr-3 rounded-full transition-all hover:bg-gray-strokes/50"
          >
            <UiIcon name="chevron_left_thin_s" class="h-11 w-11 text-primary" />
          </button>
          <button
            ref="nextEl"
            :aria-label="$t('nextSlide')"
            class="rounded-full transition-all hover:bg-gray-strokes/50"
          >
            <UiIcon
              name="chevron_right_thin_s"
              class="h-11 w-11 text-primary"
            />
          </button>
        </div>
      </div>
      <UiScrollableSlider
        :items-list="data.slides || []"
        :slides-count="data?.slidesCount?.mobile || 2"
        class="container px-0 lg:!hidden"
        :gap-x="16"
      >
        <template #card="{ item }">
          <div class="relative">
            <component
              :is="item?.buttonUrl ? NuxtLink : 'div'"
              :to="localePath(item?.buttonUrl || '')"
              class="aspect-h-16 aspect-w-12 mb-8 block cursor-pointer lg:mb-12"
              :style="{
                filter: `brightness(${data.image_brightness_filter || 100}%)`,
              }"
            >
              <UiLazyImg
                class="h-full w-full"
                :src="item.file?.url"
                :src-mobile="item.fileMobile?.url || ''"
                :alt="item.file?.alt || item.title || 'image'"
                img-classes="object-cover"
              />
            </component>
            <h3
              v-if="item.title"
              class="font-gt-ultra-fine text-3xl tracking-[-2px] text-primary lg:text-40_50"
              :style="{ textAlign: data?.card_content_position || 'left' }"
              v-html="item.title"
            />
            <p
              v-if="item.subTitle"
              class="mt-6 text-sm leading-5 text-primary lg:text-base lg:leading-6"
              :style="{ textAlign: data?.card_content_position || 'left' }"
            >
              {{ item.subTitle }}
            </p>
            <UiButton
              v-if="item?.buttonUrl && item?.buttonLabel"
              :to="item.buttonUrl"
              class="mt-6 min-w-[210px]"
              color="secondary"
            >
              {{ item.buttonLabel }}
            </UiButton>
          </div>
        </template>
      </UiScrollableSlider>
      <div class="container max-lg:hidden">
        <Swiper
          :slides-per-view="data?.slidesCount?.mobile || 2"
          :space-between="16"
          :loop="false"
          :centered-slides="false"
          :a11y="true"
          :initial-slide="0"
          :modules="modules"
          :navigation="navigation"
          class="w-full"
          :breakpoints="{
            [screens.lg]: {
              slidesPerView: data?.slidesCount?.desktop || 3,
              centeredSlides: false,
              initialSlide: 0,
              spaceBetween: 80,
            },
          }"
          @swiper="initSwiper"
          @resize="onUpdateSwiper"
        >
          <SwiperSlide v-for="item in data.slides?.slice(0, 15)" :key="item.id">
            <div class="relative">
              <component
                :is="item?.buttonUrl ? NuxtLink : 'div'"
                :to="localePath(item?.buttonUrl || '')"
                class="aspect-h-16 aspect-w-12 mb-8 block cursor-pointer lg:mb-12"
                :style="{
                  filter: `brightness(${data.image_brightness_filter || 100}%)`,
                }"
              >
                <UiLazyImg
                  class="h-full w-full"
                  :src="item.file?.url || ''"
                  :src-mobile="item.fileMobile?.url || ''"
                  :alt="item.file?.alt || item.title || 'image'"
                  img-classes="object-cover"
                />
              </component>
              <h3
                v-if="item.title"
                class="font-gt-ultra-fine text-3xl tracking-[-2px] text-primary lg:text-40_50"
                :style="{ textAlign: data?.card_content_position || 'left' }"
                v-html="item.title"
              />
              <p
                v-if="item.subTitle"
                class="mt-6 text-sm leading-5 text-primary lg:text-base lg:leading-6"
                :style="{ textAlign: data?.card_content_position || 'left' }"
              >
                {{ item.subTitle }}
              </p>
              <UiButton
                v-if="item?.buttonUrl && item?.buttonLabel"
                :to="item.buttonUrl"
                class="mt-6 min-w-[210px]"
                color="secondary"
              >
                {{ item.buttonLabel }}
              </UiButton>
            </div>
          </SwiperSlide>
        </Swiper>
      </div>
      <div v-if="data.buttonUrl && data.buttonLabel" class="text-center">
        <UiButton :to="data.buttonUrl" color="secondary" class="mx-auto">
          {{ data.buttonLabel }}
        </UiButton>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { Swiper, SwiperSlide } from "swiper/vue";
import { Navigation } from "swiper";
import { NavigationOptions } from "swiper/types";
import { debounce } from "lodash-es";
import { BlockSliderContent } from "ecom-types";
import { NuxtLink } from "#components";

import { screens } from "~/configs/breakpointsConfig.json";

const { lgAndDown } = useAppBreakpoints();

const localePath = useLocalePathPolyfill();

const props = defineProps<{ data: BlockSliderContent }>();

const hideArrows = ref(false);

const prevEl = ref<HTMLElement | null>(null);
const nextEl = ref<HTMLElement | null>(null);

const debounceSwiper = debounce((params) => {
  hideArrows.value =
    lgAndDown.value ||
    params?.params?.slidesPerView >= (props.data?.slides?.length || 2);
}, 200);

function initSwiper(params: any) {
  debounceSwiper(params);
}

function onUpdateSwiper(params: any) {
  debounceSwiper(params);
}

const navigation = reactive({
  prevEl,
  nextEl,
  disabledClass: "opacity-20 cursor-not-allowed",
}) as NavigationOptions;

const modules = [Navigation];
</script>
